<template>
  <!-- Sits in the field's own label row (SS05971 D33). `flex: none` so it never competes with the
       label text for width — a control sharing that row is how D30's defect happened. -->
  <span class="col-freeze-summary-anchor" v-if="columns && columns.length">
    <fts-tippy
      ref="tippy"
      class="col-freeze-summary"
      trigger-tag="div"
      append-trigger-class="col-freeze-summary-toggle"
      :options="popoverOptions"
      @click.native.stop.prevent="onTriggerClick"
      @keydown.native.esc.stop="close"
    >
      <!-- aria-hidden: Material Symbols render the span's TEXT as a ligature, so without it the
           button's accessible name would start with the word "keep" (D26) -->
      <span class="material-symbols-outlined col-freeze-summary-icon" aria-hidden="true">keep</span>
      <span class="col-freeze-summary-count" aria-hidden="true">{{columns.length}}</span>
      <!-- the accessible name, visually hidden. A `title` attribute would render a second,
           native tooltip alongside tippy's — the defect D18 exists for -->
      <span class="col-freeze-summary-label">{{triggerLabel}}</span>

      <!-- Escape here as well as on the root: tippy appends the popper to document.body, so once
           shown this content is portalled OUT of the root and its keydowns never reach it. -->
      <div slot="content" class="freeze-summary" @keydown.esc.stop="close">
        <div class="freeze-summary__title">Frozen columns</div>
        <ul class="freeze-summary__list">
          <li v-for="column in columns" :key="column.fieldName" class="freeze-summary__item">
            <button
              type="button"
              class="freeze-summary__reveal"
              :title="'Scroll to ' + column.label"
              @click="$emit('reveal', column.index)"
            >{{column.label}}</button>
            <button
              type="button"
              class="freeze-summary__unfreeze"
              :title="'Unfreeze ' + column.label"
              @click="$emit('unfreeze', column.index)"
            >
              <span class="material-symbols-outlined" aria-hidden="true">keep_off</span>
              <span class="col-freeze-summary-label">Unfreeze {{column.label}}</span>
            </button>
          </li>
        </ul>
        <button
          type="button"
          class="freeze-summary__clear"
          @click="$emit('unfreeze-all')"
        >Unfreeze all</button>
      </div>
    </fts-tippy>
  </span>
</template>

<script>
import FTSTippy from './FTSTippy.vue';

/**
 * Frozen-columns indicator — SS05971 D32–D35, AC30–AC34.
 *
 * Deliberately dumb: owns no freeze state and touches no storage, renders the summary it is
 * handed and emits an index. Both hosts map the events onto their existing `toggleFreeze`, so the
 * per-form key (D16), the stored `fieldName` (D8) and the eligibility rules cannot diverge.
 *
 * Known limitation (as O2's `aria-disabled`): non-prop attributes on `fts-tippy` land on its root
 * `<div>`, not the trigger, so `aria-expanded` needs a new FTSTippy prop.
 */
export default {
  name: 'FTSTableFreezeIndicator',
  components: { FtsTippy: FTSTippy },
  props: {
    /* [{ index, fieldName, label }] in render order — tableFreeze.frozenSummary() */
    columns: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    triggerLabel() {
      return this.columns.length === 1
        ? '1 frozen column — show it'
        : `${this.columns.length} frozen columns — show them`;
    },
    /* All keys exist in FTSTippy's `defaultOptions`, whose `init()` merges only known ones — no new
       prop needed. `delay: 0` overrides a hover-tuned `[500, 100]` that reads as a dead control on
       click; `interactive` is what keeps the rows clickable. */
    popoverOptions() {
      return {
        interactive: true,
        trigger: 'click',
        placement: 'bottom-end',
        delay: 0,
        theme: 'light-border'
      };
    }
  },
  methods: {
    /* Empty on purpose. The trigger must stay a `<div>` (D37) — a `<button>` is *labelable*, so the
       `<label>` this chip sits inside adopts it and forwards every FIELD NAME click to it. The root
       handler exists only for `.stop`; tippy's own listener fires regardless. */
    onTriggerClick() {},
    /**
     * Escape closes the popover.
     *
     * `hideOnClick` is not overridable and FTSTippy has no public `hide()`, so this reaches the
     * instance directly; guarded because it is created lazily on first hover/focus.
     *
     * **Both `focus()` calls are inert** — D37 made the trigger a `<div>`, so there is nothing to
     * focus and AC34's "returns focus to the chip" is struck by decision, not pending. Kept
     * because they cost nothing and revive the moment a `role`/`tabindex` is added; there are two
     * because Escape is also app-wide (QuickView dismisses on it), so `.stop` keeps it from a
     * document-level handler and the `$nextTick` pass backstops a capture-phase listener.
     */
    close() {
      const tippy = this.$refs.tippy;
      if (tippy && tippy.tippyInstance) tippy.tippyInstance.hide();

      const toggle = this.$el.querySelector('.col-freeze-summary-toggle');
      if (!toggle) return;

      toggle.focus();
      this.$nextTick(() => toggle.focus());
    }
  }
}
</script>
